import { setCartCookie, getCartFromCookie } from './cookies';
import { DbProduct } from '../api/db';

// Type definitions
export interface CartItem {
  id: string;
  brand: string;
  name: string;
  type: string;
  colorway?: string;
  size?: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  category?: string;
  maxQuantity?: number;
}

export interface Cart {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  lastUpdated: string;
}

// Initialize empty cart
const emptyCart: Cart = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  lastUpdated: new Date().toISOString()
};

// Get the current cart from cookie or create a new one
export const getCart = (): Cart => {
  const savedCart = getCartFromCookie();
  if (!savedCart) {
    return { ...emptyCart };
  }
  return savedCart;
};

// Save cart to cookie
export const saveCart = (cart: Cart): void => {
  cart.lastUpdated = new Date().toISOString();
  setCartCookie(cart);
};

// Calculate cart totals
export const calculateCartTotals = (cart: Cart): Cart => {
  cart.totalItems = cart.items.reduce((total, item) => total + item.quantity, 0);
  cart.totalPrice = cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  return cart;
};

// Add an item to the cart
export const addToCart = (product: DbProduct, quantity = 1, imageUrl?: string): {
  cart: Cart;
  success: boolean;
  message?: string;
} => {
  const cart = getCart();
  
  // Check if the item already exists in the cart
  const existingItemIndex = cart.items.findIndex(item => item.id === product.id);
  
  // Check available stock (assuming product has an 'availableQuantity' field)
  const availableQuantity = product.availableQuantity || 1; // Default to 1 if not specified
  
  if (existingItemIndex >= 0) {
    // Calculate new quantity if item already exists
    const newQuantity = cart.items[existingItemIndex].quantity + quantity;
    
    // Check if adding this quantity would exceed available stock
    if (newQuantity > availableQuantity) {
      return {
        cart,
        success: false,
        message: `The maximum quantity of this item (${availableQuantity}) is already in your cart.`
      };
    }
    
    // Increase quantity if within stock limits
    cart.items[existingItemIndex].quantity = newQuantity;
  } else {
    // Check if the quantity to add exceeds available stock
    if (quantity > availableQuantity) {
      return {
        cart,
        success: false,
        message: `Sorry, only ${availableQuantity} of this item is available.`
      };
    }
    
    // Add new item
    const newItem: CartItem = {
      id: product.id,
      brand: product.brand,
      name: `${product.brand} ${product.type}`,
      type: product.type,
      colorway: product.colorway,
      size: product.size,
      price: product.price,
      quantity: quantity,
      imageUrl: imageUrl,
      category: product.type, // Use product type as category
      maxQuantity: availableQuantity // Store max quantity for future reference
    };
    
    cart.items.push(newItem);
  }
  
  // Calculate totals and save
  const updatedCart = calculateCartTotals(cart);
  saveCart(updatedCart);
  return {
    cart: updatedCart,
    success: true
  };
};

// Update an item quantity in the cart
export const updateCartItemQuantity = (productId: string, quantity: number): {
  cart: Cart;
  success: boolean;
  message?: string;
} => {
  const cart = getCart();
  
  // Find the item in the cart
  const itemIndex = cart.items.findIndex(item => item.id === productId);
  
  if (itemIndex >= 0) {
    const item = cart.items[itemIndex];
    const maxQuantity = item.maxQuantity || 1; // Use stored max quantity or default to 1
    
    if (quantity <= 0) {
      // Remove item if quantity is 0 or negative
      cart.items = cart.items.filter(item => item.id !== productId);
    } else if (quantity > maxQuantity) {
      // Return error if trying to set quantity higher than available
      return {
        cart,
        success: false,
        message: `Sorry, only ${maxQuantity} of this item is available.`
      };
    } else {
      // Update quantity
      cart.items[itemIndex].quantity = quantity;
    }
  }
  
  // Calculate totals and save
  const updatedCart = calculateCartTotals(cart);
  saveCart(updatedCart);
  return {
    cart: updatedCart,
    success: true
  };
};

// Remove an item from the cart
export const removeFromCart = (productId: string): Cart => {
  const cart = getCart();
  
  // Remove item
  cart.items = cart.items.filter(item => item.id !== productId);
  
  // Calculate totals and save
  const updatedCart = calculateCartTotals(cart);
  saveCart(updatedCart);
  return updatedCart;
};

// Clear the entire cart
export const clearCart = (): Cart => {
  const newCart = { ...emptyCart, lastUpdated: new Date().toISOString() };
  saveCart(newCart);
  return newCart;
};

export default {
  getCart,
  addToCart,
  updateCartItemQuantity,
  removeFromCart,
  clearCart
}; 